import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  RadioGroup,
  Radio,
  Wrap,
  WrapItem,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface RadioButtonQuestionProps {
  question: Question;
}

export const validate = async (
  value: string,
  question: Question
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(value);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const RadioButtonQuestion = ({
  question
}: RadioButtonQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || null;

  const handleChange = (value: string): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: parseInt(value)
      })
    );
  };

  return (
    <FormControl
      my={4}
      as="fieldset"
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel as="legend">
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <RadioGroup
        onChange={handleChange}
        value={answer}
        size={{ base: "lg", lg: "md" }}
      >
        <Wrap
          spacing={4}
          direction={{
            base: "column",
            lg: question.horizontal ? "row" : "column"
          }}
        >
          {[...question.questionOptions]
            .filter((option) => !option.deleted)
            .sort((a, b) => a.sequence - b.sequence)
            .map((option) => (
              <WrapItem key={option.id}>
                <Radio
                  pl={1}
                  key={option.id}
                  value={option.id}
                  name="radioButtonQuestion-radio"
                  data-testid={`radioButtonQuestion-${option.name}`}
                >
                  {option.name}
                </Radio>
              </WrapItem>
            ))}
        </Wrap>
      </RadioGroup>
      {error.length > 0 && (
        <FormErrorMessage data-testid="radioButtonQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default RadioButtonQuestion;
